import { useCallback, useState, type ReactNode } from 'react';

/**
 * SF-T-330: a task's title with a small prefix on its first line (on phones, TaskDetailView puts the task's
 * number and mirrored ref there, as the top row has no room for them). The prefix sits over the title's top left,
 * at most 70% of the width (a long ref shortens with …), and the title's first line is indented past it, so the
 * rest wraps under it at full width. `children` renders the title (a Textarea) given that indent in px.
 */
export function TaskPrefixedTitle({ prefix, children }: { prefix: ReactNode; children: (indent: number) => ReactNode }) {
  const [width, setWidth] = useState(0);
  // Its width from a ResizeObserver, which reports before the first paint: the title never jumps.
  const measure = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(el.offsetWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  if (!prefix) return children(0);
  return (
    <div className="relative">
      {/* top-[11px]: on the baseline of the title's first line (text-xl, leading-snug, py-1). */}
      <span
        ref={measure}
        className="absolute left-0 top-[11px] z-10 flex max-w-[70%] items-baseline gap-1 text-xs text-muted-foreground"
        data-title-prefix
      >
        {prefix}
      </span>
      {children(width ? width + 6 : 0)}
    </div>
  );
}
